import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "../api";
import {
  Flame, Check, X, Award, Trophy, CalendarDays, Loader2, ChevronRight,
} from "lucide-react";
import usePageTitle from "../hooks/usePageTitle";

/**
 * StudentSprint — 100-day daily skill-sprint UI wired to
 * ``/api/sprint/today``, ``/api/sprint/today/answer``, ``/api/sprint/me``,
 * and ``/api/sprint/leaderboard`` (see `backend/gamification.py`).
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────────┐
 *   │ Streak stats · Today's challenge · Answer form          │
 *   │ 30-day heatmap                                          │
 *   │ Global 30-day leaderboard                               │
 *   └─────────────────────────────────────────────────────────┘
 */
export default function StudentSprint() {
  usePageTitle("100-Day Skill Sprint");
  const [challenge, setChallenge] = useState(null);
  const [me, setMe] = useState(null);
  const [leaders, setLeaders] = useState([]);
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const [c, m, l] = await Promise.all([
        api.get("/sprint/today"),
        api.get("/sprint/me"),
        api.get("/sprint/leaderboard?limit=10"),
      ]);
      setChallenge(c.data);
      setMe(m.data);
      setLeaders(l.data);
      if (c.data?.already_answered) {
        setResult({ correct: !!c.data.correct, replay: true });
      }
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!answer.trim()) { setErr("Pick an answer first."); return; }
    setSubmitting(true);
    setErr("");
    try {
      const r = await api.post("/sprint/today/answer", {
        challenge_id: challenge.id, answer: answer.trim(),
      });
      setResult({ correct: r.data.correct, expected: r.data.expected, streak: r.data.streak });
      // Refresh streak stats
      const m = await api.get("/sprint/me");
      setMe(m.data);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="student-sprint">
      <div className="rounded-3xl p-8 text-white relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #FF6B35 0%, #FF3D7F 100%)" }}>
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/90 inline-flex items-center gap-1">
          <Flame size={12} /> 100-day sprint
        </span>
        <h1 className="font-display font-black text-3xl sm:text-4xl mt-2 leading-tight">
          One challenge a day. Streaks recruiters can see.
        </h1>
        <p className="text-white/85 text-sm mt-2 max-w-2xl">
          Answer today's micro-challenge to keep your streak alive. Every correct
          answer counts toward your public "learning signal" on your profile.
        </p>
        {me && (
          <div className="grid grid-cols-3 gap-3 mt-6 max-w-md">
            <Stat icon={Flame} label="Current" value={me.current_streak} testid="stat-current"/>
            <Stat icon={Trophy} label="Longest" value={me.longest_streak} testid="stat-longest"/>
            <Stat icon={Award} label="Correct" value={me.total_correct} testid="stat-total"/>
          </div>
        )}
      </div>

      {err && <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm p-3" data-testid="sprint-error">{err}</div>}

      {loading && (
        <div className="card card-pad text-muted flex items-center gap-2" data-testid="sprint-loading">
          <Loader2 size={16} className="animate-spin"/> Loading today's challenge…
        </div>
      )}

      {!loading && challenge && (
        <div className="card card-pad" data-testid="sprint-challenge">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className="eyebrow">Day {challenge.day_index} · CoE {challenge.coe_lab}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
              challenge.difficulty === "easy" ? "bg-emerald-100 text-emerald-700" :
              challenge.difficulty === "medium" ? "bg-amber-100 text-amber-700" :
              "bg-red-100 text-red-700"
            }`}>{challenge.difficulty}</span>
          </div>
          <h2 className="font-display font-bold text-2xl leading-snug mb-4">{challenge.question}</h2>

          {result?.replay ? (
            <div className={`rounded-2xl p-4 ${
              result.correct ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
                             : "bg-red-50 border border-red-200 text-red-800"
            }`} data-testid="sprint-already-answered">
              You already answered today — {result.correct ? "correctly. Nice." : "incorrectly. Try again tomorrow."}
            </div>
          ) : result ? (
            <div className={`rounded-2xl p-4 ${
              result.correct ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
                             : "bg-red-50 border border-red-200 text-red-800"
            }`} data-testid="sprint-result">
              {result.correct ? (
                <div className="flex items-center gap-2 font-semibold"><Check size={16}/> Correct! Streak: {result.streak}</div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 font-semibold mb-1"><X size={16}/> Not quite.</div>
                  {result.expected && <div className="text-sm">The right answer was <strong>{result.expected}</strong>.</div>}
                </div>
              )}
            </div>
          ) : challenge.type === "mcq" ? (
            <div className="grid gap-2 sm:grid-cols-2">
              {(challenge.options || []).map((o) => (
                <label key={o}
                  className={`rounded-xl border p-3 cursor-pointer transition ${
                    answer === o
                      ? "border-signal bg-blue-50"
                      : "border-slate-200 hover:border-signal/40 hover:bg-paper"
                  }`}
                  style={{ borderColor: answer === o ? "var(--signal)" : "var(--rule)" }}
                  data-testid={`sprint-opt-${o.replace(/\s+/g, "_")}`}
                >
                  <input type="radio" className="mr-2" name="opt" checked={answer === o}
                    onChange={() => setAnswer(o)} />
                  {o}
                </label>
              ))}
            </div>
          ) : (
            <div className="flex gap-2">
              <button type="button"
                className={`flex-1 rounded-xl border p-3 font-semibold transition ${
                  answer === "true" ? "border-signal bg-blue-50 text-signal" : "border-slate-200 hover:border-signal/40"
                }`}
                onClick={() => setAnswer("true")}
                data-testid="sprint-opt-true"
              >True</button>
              <button type="button"
                className={`flex-1 rounded-xl border p-3 font-semibold transition ${
                  answer === "false" ? "border-signal bg-blue-50 text-signal" : "border-slate-200 hover:border-signal/40"
                }`}
                onClick={() => setAnswer("false")}
                data-testid="sprint-opt-false"
              >False</button>
            </div>
          )}

          {!result && !challenge.already_answered && (
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !answer}
              className="btn-primary mt-4"
              data-testid="sprint-submit"
            >
              {submitting ? "Submitting…" : "Submit answer"} <ChevronRight size={14}/>
            </button>
          )}
        </div>
      )}

      {me?.last_30_days && <Heatmap days={me.last_30_days} />}
      {leaders?.length > 0 && <Leaderboard rows={leaders} />}
    </div>
  );
}

function Stat({ icon: Ic, label, value, testid }) {
  return (
    <div className="rounded-2xl bg-white/15 backdrop-blur px-4 py-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/90">
        <Ic size={11}/> {label}
      </div>
      <div className="font-display font-black text-2xl mt-1">{value}</div>
    </div>
  );
}

function Heatmap({ days }) {
  return (
    <div className="card card-pad" data-testid="sprint-heatmap">
      <div className="flex items-center gap-1.5 mb-3">
        <CalendarDays size={14} className="text-signal"/>
        <h3 className="font-display font-bold text-sm uppercase tracking-wider">Last 30 days</h3>
      </div>
      <div className="grid gap-1" style={{ gridTemplateColumns: "repeat(30, minmax(0,1fr))" }}>
        {days.map((d) => {
          const cls = d.correct ? "bg-emerald-500"
                    : d.attempted ? "bg-amber-400"
                    : "bg-slate-200";
          return (
            <div key={d.date}
              className={`aspect-square rounded ${cls}`}
              title={`${d.date} — ${d.correct ? "correct" : d.attempted ? "attempted" : "skipped"}`}
              data-testid={`sprint-day-${d.date}`}
            />
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-3 text-[10px] text-muted">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500"/> Correct</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-400"/> Attempted</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-slate-200"/> Skipped</span>
      </div>
    </div>
  );
}

function Leaderboard({ rows }) {
  return (
    <div className="card card-pad" data-testid="sprint-leaderboard">
      <div className="flex items-center gap-1.5 mb-3">
        <Trophy size={14} className="text-signal"/>
        <h3 className="font-display font-bold text-sm uppercase tracking-wider">
          Top of the sprint · Last 30 days
        </h3>
      </div>
      <div className="grid gap-1">
        {rows.map((r) => (
          <div key={r.user_id}
            className="flex items-center gap-3 py-2 border-b last:border-0"
            style={{ borderColor: "var(--rule)" }}
            data-testid={`sprint-rank-${r.rank}`}
          >
            <div className="w-8 text-center font-display font-black text-sm text-muted">#{r.rank}</div>
            <div className="flex-1 font-semibold text-sm truncate">{r.name}</div>
            <div className="text-signal font-black tabular-nums">{r.score}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
